import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../styles/form.css";

const Form = ({ jobTitle, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    resume: null,
  });

  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef(null);

  // Focus management: move focus into the dialog on open, trap Tab, close on Escape
  useEffect(() => {
    const dialog = dialogRef.current;
    const first = dialog && dialog.querySelector("input, textarea, button");
    if (first) first.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !dialog) return;
      const focusable = dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const firstEl = focusable[0];
      const lastEl = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Handle text input
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle phone input
  const handlePhoneChange = (value) => {
    setFormData({ ...formData, phone: value });
  };

  // Handle file upload
  const handleFileChange = (e) => {
    setFormData({ ...formData, resume: e.target.files[0] });
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setStatus("");

    const data = new FormData();
    data.append("name", formData.name);
    data.append("email", formData.email);
    data.append("phone", formData.phone || "");
    if (jobTitle) data.append("position", jobTitle);
    if (formData.resume) data.append("resume", formData.resume);

    try {
      const API_BASE_URL =
        process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

      const response = await axios.post(`${API_BASE_URL}/api/applications/`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 201 || response.status === 200) {
        setStatus("success");
        setFormData({ name: "", email: "", phone: "", resume: null });
      } else {
        setStatus("We couldn't submit your application. Please try again.");
      }
    } catch (error) {
      console.error("Submission error:", error.response || error.message);
      setStatus(
        "We couldn't submit your application right now. Please try again, or email us directly at osama@intellusionai.com."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="apply-form-title"
        ref={dialogRef}
      >
        <button className="close-modal" onClick={onClose} aria-label="Close">
          ×
        </button>

        <h2 className="form-title" id="apply-form-title">
          {jobTitle ? `Apply for ${jobTitle}` : "Apply Now"}
        </h2>

        {status === "success" ? (
          <div className="status-msg status-success" role="status">
            <p>Thanks. Your application has been submitted successfully.</p>
            <button type="button" className="submit-btn" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <>
        {status && status !== "success" && (
          <p className="status-msg status-error" role="alert">
            {status}
          </p>
        )}

        <form
          onSubmit={handleSubmit}
          className="application-form"
          encType="multipart/form-data"
        >
          <label>Full Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />

          <label>Email Address</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />

          <label>Phone Number</label>
          <PhoneInput
            country="pk"
            value={formData.phone}
            onChange={handlePhoneChange}
            inputStyle={{
              width: "100%",
              padding: "0.9rem",
              borderRadius: "10px",
              background: "#141417",
              color: "#f4f4f5",
              border: "1px solid #232327",
              caretColor: "#f4f4f5",
            }}
            buttonStyle={{
              border: "1px solid #232327",
              background: "#141417",
            }}
            dropdownStyle={{
              background: "#141417",
              color: "#f4f4f5",
            }}
          />


          <label>Upload Resume (PDF/DOC/DOCX)</label>
          <input
            type="file"
            name="resume"
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx"
          />

          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit Application"}
          </button>
        </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Form;
